<template>
  <div class="z-[100] top-0 fixed">
    <transition name="menu">
      <div
        v-if="open"
        class="z-20 transition-all duration-300 bg-n-900/80 backdrop-blur-sm absolute top-0 left-0 w-screen h-screen flex justify-end overflow-hidden"
        @click="emit('close')"
      >
        <div
          class="sidebar transition-all bg-n-800 backdrop-blur-sm w-80 h-full overflow-y-scroll pt-safe"
          @click.stop
        >
          <div class="relative text-n-0 flex flex-col px-3 h-full">
            <div class="absolute top-0 right-0 h-14 flex items-center">
              <l-button
                size="medium"
                icon="close"
                type="text"
                color="white"
                @click="$emit('close')"
              />
            </div>
            <div v-if="importantNotifications.length > 0" class="flex flex-col mt-10 py-4 w-full">
              <div class="font-title font-bold text-base leading-5 mb-6">
                {{ t('notifications.important-notifications') }}
              </div>
              <transition-group
                class="notification-list flex flex-col items-center"
                tag="div"
                name="list"
                appear
                @before-leave="beforeLeave"
              >
                <l-notification
                  v-for="notification in importantNotifications"
                  :key="notification.id"
                  class="notification mb-2 pointer-events-auto"
                  v-bind="notification"
                  :close="false"
                />
              </transition-group>
            </div>
            <div
              class="flex flex-col py-4 w-full"
              :class="{ 'mt-12': importantNotifications.length <= 0 }"
            >
              <div class="flex justify-between items-center mb-6">
                <div class="font-title font-bold text-base leading-5">
                  {{ t('notifications.notifications') }}
                </div>
                <div
                  v-if="notifications.length > 0"
                  class="text-v cursor-pointer underline text-sm"
                  @click="clearAll"
                >
                  {{ t('notifications.clear-all') }}
                </div>
              </div>
              <transition-group
                v-if="notifications.length > 0"
                class="notification-list flex flex-col items-center"
                tag="div"
                name="list"
                appear
                @before-leave="beforeLeave"
              >
                <l-notification
                  v-for="notification in standardNotifications"
                  :key="notification.id"
                  class="notification mb-2 pointer-events-auto"
                  v-bind="notification"
                  @close="() => close(notification.id)"
                />
              </transition-group>
              <l-empty-case v-else type="empty" class="mt-8">
                {{ t('notifications.empty-notifications') }}
              </l-empty-case>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LEmptyCase, LNotification } from '@last/core-ui/paprika'

import { useNotifications } from '@/composables/useNotifications'

const { notifications, close } = useNotifications()

defineProps<{
  open: boolean
}>()

const emit = defineEmits(['close'])

const { t } = useI18n()

function clearAll() {
  standardNotifications.value.forEach(n => close(n.id))
}

const importantNotifications = computed(() => notifications.filter(n => n.important))

const standardNotifications = computed(() => notifications.filter(n => !n.important))

const beforeLeave = (element: Element) => {
  const el = element as HTMLElement
  const { marginLeft, marginTop, width, height } = window.getComputedStyle(el)

  el.style.left = `${el.offsetLeft - parseFloat(marginLeft)}px`
  el.style.top = `${el.offsetTop - parseFloat(marginTop)}px`
  el.style.width = width
  el.style.height = height
}
</script>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
}

.menu-enter-active .sidebar,
.menu-leave-active .sidebar {
  transition: transform 0.3s ease-in-out;
}

.menu-enter-from .sidebar,
.menu-leave-to .sidebar {
  transform: translateX(100%);
}

.list-enter-from {
  opacity: 0;
  transform: scale(0.6);
}

.list-enter-active {
  transition: all 0.4s ease;
}

.list-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

.list-leave-active {
  transition: all 0.3s ease;
  position: absolute;
}

.list-move {
  transition: all 0.3s ease;
}
</style>
